import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Container, Paper, TextField, Button, Typography,
  Box, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { ticketService } from '../services/ticketService';
import { categoryService } from '../services/categoryService';
import api from '../services/api';
import { toast } from 'react-toastify';

const validationSchema = Yup.object({
  title: Yup.string().required('Titel ist erforderlich'),
  description: Yup.string().required('Beschreibung ist erforderlich'),
  priority: Yup.string().required('Priorität ist erforderlich'),
  category_id: Yup.number().required('Kategorie ist erforderlich')
});

const CreateTicket = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [kcs, setKCs] = useState([]);

  useEffect(() => {
    loadCategories();
    loadKCs();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await categoryService.getAllCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const loadKCs = async () => {
    try {
      const response = await api.get('/kcs');
      const kcsData = response.data;
      
      // Load locations for each KC
      const kcsWithLocations = await Promise.all(
        kcsData.map(async (kc) => {
          try {
            const locResponse = await api.get(`/kcs/${kc.id}/locations`);
            return { ...kc, locations: locResponse.data };
          } catch (error) {
            console.error(`Failed to load locations for KC ${kc.id}`);
            return { ...kc, locations: [] };
          }
        })
      );
      
      setKCs(kcsWithLocations);
    } catch (error) {
      console.error('Failed to load KCs');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await ticketService.createTicket(values);
      toast.success('Ticket erfolgreich erstellt');
      navigate('/tickets');
    } catch (error) {
      toast.error('Fehler beim Erstellen des Tickets');
    }
    setSubmitting(false);
  };

  return (
    <Container maxWidth="md">
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/tickets')}
        sx={{ mb: 2 }}
      >
        Zurück zu Tickets
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Neues Ticket erstellen
        </Typography>

        <Formik
          initialValues={{
            title: '',
            description: '',
            priority: 'medium',
            category_id: '',
            competence_center_id: '',
            location: '',
            plc_device_id: '',
            serial_number: '',
            planner_group: '',
            external_ticket_number: ''
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <Field
                as={TextField}
                fullWidth
                margin="normal"
                name="external_ticket_number"
                label="Bayernwerk Ticket-Nr. (Optional)"
                placeholder="z.B. 20250610-0743"
              />
              
              <Field
                as={TextField}
                fullWidth
                margin="normal"
                name="title"
                label="Titel"
                error={touched.title && !!errors.title}
                helperText={touched.title && errors.title}
              />

              <Field
                as={TextField}
                fullWidth
                multiline
                rows={4}
                margin="normal"
                name="description"
                label="Beschreibung"
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Priorität</InputLabel>
                <Select
                  name="priority"
                  value={values.priority}
                  label="Priorität"
                  onChange={(e) => setFieldValue('priority', e.target.value)}
                >
                  <MenuItem value="low">Niedrig</MenuItem>
                  <MenuItem value="medium">Mittel</MenuItem>
                  <MenuItem value="high">Hoch</MenuItem>
                  <MenuItem value="critical">Kritisch</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                margin="normal"
                error={touched.category_id && !!errors.category_id}
              >
                <InputLabel>Kategorie</InputLabel>
                <Select
                  name="category_id"
                  value={values.category_id}
                  label="Kategorie"
                  onChange={(e) => setFieldValue('category_id', e.target.value)}
                >
                  <MenuItem value="">Kategorie auswählen</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>Kompetenz-Center</InputLabel>
                <Select
                  name="competence_center_id"
                  value={values.competence_center_id}
                  label="Kompetenz-Center"
                  onChange={(e) => {
                    setFieldValue('competence_center_id', e.target.value);
                    setFieldValue('location', '');
                  }}
                >
                  <MenuItem value="">KC auswählen</MenuItem>
                  {kcs.map((kc) => (
                    <MenuItem key={kc.id} value={kc.id}>
                      {kc.code} - {kc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {values.competence_center_id && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Standort</InputLabel>
                  <Select
                    name="location"
                    value={values.location}
                    label="Standort"
                    onChange={(e) => setFieldValue('location', e.target.value)}
                  >
                    <MenuItem value="">Standort auswählen</MenuItem>
                    {kcs
                      .find(kc => kc.id === values.competence_center_id)
                      ?.locations?.map((location) => (
                        <MenuItem key={location.id} value={location.full_name}>
                          {location.full_name} (Gruppe {location.planner_group})
                        </MenuItem>
                      )) || []}
                  </Select>
                </FormControl>
              )}

              <Field
                as={TextField}
                fullWidth
                margin="normal"
                name="plc_device_id"
                label="PLC Geräte-ID (Optional)"
              />

              <Field
                as={TextField}
                fullWidth
                margin="normal"
                name="serial_number"
                label="Seriennummer (Optional)"
              />

              <Box mt={3} display="flex" gap={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  Ticket erstellen
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/tickets')}
                >
                  Abbrechen
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default CreateTicket;